import { DevicePageRespData } from "api/types/device";
import { t } from "i18next";

import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import Table from "widgets/controls/tables/genericTable";
import { reportScheduledDelete, reportScheduledPage } from "api/report";
import { MdRefresh } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import { useSearchParams } from "react-router-dom";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import defineScheduledTablecolumns, {
  ScheduledPageTableModel,
} from "models/tables/scheduledPage";
import { setLoading } from "store/appSlice";
import ScheduleRecipientsDialog from "widgets/dialogs/schedule";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";

const ScheduleReportsView = () => {
  const dispatch = useDispatch();
  const { notify } = useNotificationDialog();
  const [searchParams] = useSearchParams();
  // Extract the filter parameter
  const id = searchParams.get("id");

  const columns = defineScheduledTablecolumns(
    // View
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setContext("view");
      setIsOpen(true);
    },
    // Recipients
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setContext("edit");
      setIsOpen(true);
    },
    // Remove
    (info) => {
      const row = info.row.original;
      notify({
        header: t("dialog.warning.title"),
        message: t("dialog.scheduled.delete.message"),
        okText: t("button.action.remove"),
        cancelText: t("button.action.cancel"),
        onOk: () => {
          doDelete(row?._id);
        },
        onCancel: () => {},
      });
    }
  );

  const [filter, setFilter] = useState("");
  const [state, setState] = useState({
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "created" }],
    totCount: 0,
    pageCount: 0,
    siteId: id || "",
  });

  const [dataSource, setDataSource] = useState<DevicePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ScheduledPageTableModel>();
  const [context, setContext] = useState<"new" | "edit" | "view">("new");

  const updatePage = (refresh: boolean = false) => {
    setIsOpen(false);
    setSelected(undefined);
    setContext("new");
    if (refresh) {
      fetchData();
    }
  };

  const fetchData = useCallback(async () => {
    const { paginationState, sortingState, siteId } = state;
    if (siteId) {
      setIsLoading(true);
      const { status, data, error, isLoading } = await reportScheduledPage({
        siteId: siteId,
        search: filter,
        pageIndex: paginationState.pageIndex,
        pageSize: paginationState.pageSize,
        desc: sortingState[0].desc,
        sort: sortingState[0].id,
      });
      setIsLoading(isLoading);
      if (status && status === 200 && !error) {
        setDataSource(data.data || []);
        setState((prevState) => ({
          ...prevState,
          paginationState: {
            pageIndex: data.currentPage - 1,
            pageSize: paginationState.pageSize,
          },
          totCount: data.totalCount,
          pageCount: data.totalPages,
        }));
      } else {
        notifySimple(t("toast.failed.data"), "error");
      }
    } else {
      setDataSource([]);
    }
  }, [
    state.siteId,
    filter,
    state.paginationState.pageIndex,
    state.paginationState.pageSize,
    state.sortingState,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const doDelete = async (id: string) => {
    dispatch(setLoading(true));

    const { status } = await reportScheduledDelete(id);
    dispatch(setLoading(false));
    if (status === 200) {
      notifySimple(
        t("notification.report.scheduled.delete.success"),
        "success"
      );
      updatePage(true);
    } else {
      notifySimple(t("notification.report.scheduled.delete.error"), "error");
    }
  };

  return (
    <>
      {isOpen && (
        <ScheduleRecipientsDialog
          isOpen={isOpen}
          onClose={() => updatePage(false)}
          onSuccess={() => updatePage(true)}
          context={context}
          values={selected}
        />
      )}
      <div className="mt-4">
        <Table
          offset={70}
          filterbar={
            <div className="flex h-full w-full flex-col gap-y-3">
              <div className="flex justify-between">
                <div className="">
                  <SitesCombobox
                    filter
                    id="device-select-site"
                    placeholder={t("form.placeholder.select.site")}
                    variant="solid"
                    color="secondary"
                    value={""}
                    onChange={(item) => {
                      setState((prevState) => ({
                        ...prevState,
                        siteId: item,
                        paginationState: {
                          pageIndex: 0,
                          pageSize: state.paginationState.pageSize,
                        },
                      }));
                    }}
                    sizes="md"
                    rounded="rounded-xl"
                  />
                </div>
                <CircleButton
                  onClick={() => {
                    updatePage(true);
                  }}
                  icon={MdRefresh}
                  size="md"
                  color="secondary"
                  variant="ghost"
                  tooltip={t("tooltip.refresh")}
                  tooltipPlacement="bottom"
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          noDataLabel={
            state.siteId
              ? t("table.status.noData")
              : t("toast.no.site.selected")
          }
          loading={isLoading}
          data={dataSource}
          columns={columns}
          pagination={state.paginationState}
          paginationOptions={{
            onPaginationChange: (pagination) => {
              const page =
                typeof pagination === "function"
                  ? pagination(state.paginationState)
                  : pagination;
              setState((prevState) => ({
                ...prevState,
                paginationState: page,
              }));
            },
            rowCount: state.totCount,
            pageCount: state.pageCount,
          }}
          sorting={state.sortingState}
          onSortingChange={(updaterOrValue) => {
            const newSortingState =
              typeof updaterOrValue === "function"
                ? updaterOrValue(state.sortingState)
                : updaterOrValue;
            if (newSortingState && newSortingState.length > 0) {
              setState((prevState) => ({
                ...prevState,
                sortingState: newSortingState,
              }));
            } else {
              setState((prevState) => ({
                ...prevState,
                sortingState: [
                  { id: "created", desc: !state.sortingState[0].desc },
                ],
              }));
            }
          }}
        />
      </div>
    </>
  );
};

export default ScheduleReportsView;
